import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, MessageCircle, Plus, Image as ImageIcon, ShoppingBag, Trash2, Send, X, Link2 } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PILLARS, PillarBadge, GoldBadge } from "@/constants/pillars";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Composer({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("post");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [pillar, setPillar] = useState("asset");
  const [imageUrl, setImageUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const uploadImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd);
      setImageUrl(data.url);
      toast.success("Immagine caricata");
    } catch { toast.error("Upload fallito"); }
  };

  const submit = async () => {
    if (!text.trim() && type === "post") return toast.error("Scrivi qualcosa");
    setBusy(true);
    try {
      const { data } = await api.post("/posts", {
        type, text, title: title || undefined, price: parseFloat(price) || 0,
        pillar, image_url: imageUrl,
      });
      onCreated(data);
      setOpen(false);
      setText(""); setTitle(""); setPrice(""); setImageUrl(null); setType("post");
      toast.success("Pubblicato!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="open-composer-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 md:bottom-8 right-4 md:right-[calc(50%-min(42rem,50vw)+1rem)] z-40 h-14 w-14 rounded-full bg-black text-white shadow-lg flex items-center justify-center hover:bg-neutral-800 active:scale-95 transition-all"
      >
        <Plus size={26} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Crea contenuto</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            {["post", "request", "offer"].map((t) => (
              <button key={t} data-testid={`composer-type-${t}`} onClick={() => setType(t)}
                className={`flex-1 h-9 rounded-full text-xs font-semibold capitalize transition-colors ${type === t ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"}`}>
                {t === "post" ? "Post" : t === "request" ? "Richiesta" : "Offerta"}
              </button>
            ))}
          </div>
          {(type === "offer" || type === "request") && (
            <Input data-testid="composer-title" placeholder="Titolo" value={title} onChange={(e) => setTitle(e.target.value)} className="h-11" />
          )}
          <Textarea data-testid="composer-text" placeholder="Cosa vuoi condividere?" value={text} onChange={(e) => setText(e.target.value)} rows={3} />
          <div className="flex gap-2">
            <Select value={pillar} onValueChange={setPillar}>
              <SelectTrigger data-testid="composer-pillar" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{PILLARS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            {type === "offer" && (
              <Input data-testid="composer-price" type="number" placeholder="Prezzo €" value={price} onChange={(e) => setPrice(e.target.value)} className="h-11 w-32" />
            )}
          </div>
          {imageUrl ? (
            <div className="relative">
              <img src={fileUrl(imageUrl)} alt="" className="rounded-xl w-full h-40 object-cover" />
              <button onClick={() => setImageUrl(null)} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1"><X size={16} /></button>
            </div>
          ) : (
            <button data-testid="composer-add-image" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-sm text-neutral-500 hover:text-black">
              <ImageIcon size={18} /> Aggiungi immagine
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadImg} />
          <p data-testid="composer-no-contact-hint" className="text-[11px] text-neutral-400">Niente telefono, email, link o social nei contenuti: contatti e pagamenti passano da ENBISSY.</p>
          <Button data-testid="composer-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-full bg-black text-white">
            {busy ? "Pubblico…" : "Pubblica"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PostCard({ post, onDelete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [likes, setLikes] = useState(post.likes?.length || 0);
  const [liked, setLiked] = useState(post.likes?.includes(user?.id));
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [ctext, setCtext] = useState("");

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked); setLikes(data.count);
    } catch {}
  };

  const loadComments = async () => {
    setShowComments(!showComments);
    if (!showComments) {
      const { data } = await api.get(`/posts/${post.id}/comments`);
      setComments(data);
    }
  };

  const addComment = async () => {
    if (!ctext.trim()) return;
    const { data } = await api.post(`/posts/${post.id}/comments`, { text: ctext });
    setComments([...comments, data]);
    setCtext("");
  };

  const del = async () => {
    await api.delete(`/posts/${post.id}`);
    onDelete(post.id);
    toast.success("Post eliminato");
  };

  const a = post.author || {};
  return (
    <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border-b border-neutral-200 md:border md:rounded-2xl p-4 md:mb-3" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={fileUrl(a.avatar_url)} />
          <AvatarFallback className="bg-black text-white text-xs">{(a.name || "?")[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-sm">{a.name}</span>
            {a.is_gold && <GoldBadge />}
            <span className="text-xs text-neutral-400 capitalize">· {a.role}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {post.pillar && <PillarBadge id={post.pillar} />}
            {post.type === "request" && <span className="text-[11px] font-medium text-orange-600">Richiesta</span>}
            {post.type === "offer" && <span className="text-[11px] font-medium text-emerald-600">Offerta</span>}
          </div>
        </div>
        {(post.author_id === user?.id || user?.role === "admin") && (
          <button data-testid={`delete-post-${post.id}`} onClick={del} className="text-neutral-300 hover:text-red-500 p-1"><Trash2 size={16} /></button>
        )}
      </div>

      {post.title && <h3 className="font-display font-semibold mt-3">{post.title}</h3>}
      {post.text && <p className="text-sm mt-2 whitespace-pre-wrap leading-relaxed">{post.text}</p>}
      {post.image_url && <img src={fileUrl(post.image_url)} alt="" className="mt-3 rounded-xl w-full max-h-96 object-cover" />}

      {post.type === "offer" && (
        <div className="mt-3 flex items-center justify-between bg-neutral-50 rounded-xl p-3">
          <span className="font-display font-bold text-lg">€{Number(post.price).toFixed(2)}</span>
          <Button data-testid={`buy-post-${post.id}`} onClick={() => navigate(`/marketplace/${post.offer_id}`)}
            className="h-9 rounded-full bg-black text-white text-sm">
            <ShoppingBag size={15} className="mr-1" /> Acquista
          </Button>
        </div>
      )}

      <div className="flex items-center gap-5 mt-3 text-neutral-500">
        <button data-testid={`like-post-${post.id}`} onClick={toggleLike} className="flex items-center gap-1.5 text-sm hover:text-black min-h-[36px]">
          <Heart size={18} className={liked ? "fill-red-500 text-red-500" : ""} /> {likes}
        </button>
        <button data-testid={`comment-post-${post.id}`} onClick={loadComments} className="flex items-center gap-1.5 text-sm hover:text-black min-h-[36px]">
          <MessageCircle size={18} /> {post.comment_count || comments.length || 0}
        </button>
      </div>

      {showComments && (
        <div className="mt-3 space-y-2">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2 text-sm">
              <span className="font-semibold">{c.author?.name}:</span>
              <span className="text-neutral-700">{c.text}</span>
            </div>
          ))}
          <div className="flex gap-2 mt-2">
            <Input data-testid={`comment-input-${post.id}`} value={ctext} onChange={(e) => setCtext(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Commenta…" className="h-9 text-sm" />
            <Button data-testid={`comment-send-${post.id}`} onClick={addComment} className="h-9 w-9 p-0 rounded-full bg-black"><Send size={15} /></Button>
          </div>
        </div>
      )}
    </motion.article>
  );
}

export default function FeedPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matchCount, setMatchCount] = useState(0);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/posts", { params: filter ? { pillar: filter } : {} });
    setPosts(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/matches/mine").then((r) => setMatchCount(r.data.length)).catch(() => {}); }, []);

  return (
    <div>
      <button
        data-testid="feed-matches-banner"
        onClick={() => navigate("/agganci")}
        className="mx-4 mt-4 md:mx-0 w-[calc(100%-2rem)] md:w-full flex items-center gap-3 rounded-2xl bg-[#0a0a0a] text-white p-4 hover:bg-neutral-900 transition-colors"
      >
        <Link2 size={22} className="text-[#d4af37] shrink-0" />
        <div className="flex-1 text-left">
          <p className="font-semibold text-sm">{t("matches_title")}</p>
          <p className="text-xs text-neutral-400">{matchCount > 0 ? `${matchCount} ${t("matches_found")}` : t("matches_none")}</p>
        </div>
        {matchCount > 0 && <span className="bg-[#d4af37] text-black text-xs font-bold rounded-full px-2 py-0.5">{matchCount}</span>}
      </button>

      <div className="px-4 py-3 flex gap-2 overflow-x-auto sticky top-14 md:top-0 z-30 bg-white/95 backdrop-blur-md border-b border-neutral-100">
        <button data-testid="filter-all" onClick={() => setFilter(null)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${!filter ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"}`}>{t("filter_all")}</button>
        {PILLARS.map((p) => (
          <button key={p.id} data-testid={`filter-${p.id}`} onClick={() => setFilter(p.id)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${filter === p.id ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"}`}>{p.name}</button>
        ))}
      </div>
      <div className="md:pt-3">
        {loading ? (
          <p className="text-center text-neutral-400 py-10 text-sm">Caricamento…</p>
        ) : posts.length === 0 ? (
          <p className="text-center text-neutral-400 py-10 text-sm">{t("empty_feed")}</p>
        ) : (
          posts.map((p) => <PostCard key={p.id} post={p} onDelete={(id) => setPosts(posts.filter((x) => x.id !== id))} />)
        )}
      </div>
      <Composer onCreated={(p) => setPosts([p, ...posts])} />
    </div>
  );
}
